import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { permissionService } from "../services/permissionService";
import { API_BASE, api } from "../utils/api";
import { PLAN_STATE_UPDATED_EVENT } from "../utils/planSync";

// ICONS
import { FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown, FiGrid } from "react-icons/fi";
import { IoLogoWhatsapp } from "react-icons/io5";

import producticon from "../assets/product.png";
import analyticicon from "../assets/analytic.png";
import integicon from "../assets/integ.png";
import seticon from "../assets/setting.png";
import logicon from "../assets/logout.png";
import permicon from "../assets/permission.png";
import User from "../assets/user.png";
import subscription from "../assets/subscription.png";
import invoice from "../assets/invoice.png";
import payment from "../assets/payment.png";
import nyafaz from "../assets/nyafazlogo.png";
import IBdashboard from "../assets/Ibdash.png";
import contacticon from "../assets/contact.png";
import aiicon from "../assets/ai.png";
import myorders from "../assets/orders.png";
import marketicon from "../assets/marketing.png";

// Sidebar sections group related items in the user journey.
const SIDEBAR_SECTIONS = {
  ACCOUNT: "Account & billing",
  SALES: "Sales Team",
};

const Sidebar = ({ dashboardOnly = false, expanded, setExpanded }) => {
  const navigate = useNavigate();
  const hoverTimer = useRef(null);
  const menuRef = useRef(null);
  const [embedEnabled, setEmbedEnabled] = useState(true);

  useEffect(() => {
    const checkEmbedAccess = async () => {
      const token = localStorage.getItem("userId");
      if (!token || permissionService.isPlatformAdmin()) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/session/embed-access`, {
            credentials: 'include',
            method: "GET",
          headers: {
          },
        });
        const data = await response.json();
        if (response.ok) {
          setEmbedEnabled(Boolean(data?.enabled));
        }
      } catch {
        setEmbedEnabled(false);
      }

    };

    checkEmbedAccess();
  }, []);

  useEffect(() => {
    const onPlanUpdate = () => {
      // Re-check embed access when plan changes
      (async () => {
        const token = localStorage.getItem("userId");
        if (!token || permissionService.isPlatformAdmin()) return;
        try {
          const response = await fetch(`${API_BASE}/session/embed-access`, {
            credentials: 'include',
            method: "GET",
            headers: { },
          });
          const data = await response.json();
          if (response.ok) setEmbedEnabled(Boolean(data?.enabled));
        } catch {
          setEmbedEnabled(false);
        }
      })();
    };

    window.addEventListener(PLAN_STATE_UPDATED_EVENT, onPlanUpdate);
    return () => window.removeEventListener(PLAN_STATE_UPDATED_EVENT, onPlanUpdate);
  }, []);

  const handleLogout = async () => {
    const userId = localStorage.getItem("userId");
    try {
      if (userId) {
        await api("/logout", {
          method: "POST",
          body: JSON.stringify({ userId }),
        });
      }
    } catch {
      /* ignore */
    }
    localStorage.clear();
    navigate("/nyafaz-brand");
  };

  const scrollUp = () => {
    if (menuRef.current) {
      menuRef.current.scrollBy({ top: -100, behavior: "smooth" });
    }
  };

  const scrollDown = () => {
    if (menuRef.current) {
      menuRef.current.scrollBy({ top: 100, behavior: "smooth" });
    }
  };

  const menuItems = [
    {
      key: "overview",
      IconComponent: FiGrid,
      label: "Overview",
      to: "/nyafaz-brand/overview",
    },
    {
      key: "ibdashboard",
      icon: IBdashboard,
      label: "Dashboard",
      to: "/nyafaz-brand/ib-dashboard",
      userOnly: true,
    },
    {
      key: "admin-ops-dashboard",
      icon: IBdashboard,
      label: "Admin Dashboard",
      to: "/nyafaz-brand/admin-ops-dashboard",
      adminOnly: true,
    },
    {
      key: "analytics",
      icon: analyticicon,
      label: "Brand flow",
      to: "/nyafaz-brand/brandflow",
      permission: [14, 15],
    },
    {
      key: "productlist",
      icon: producticon,
      label: "Product list",
      to: "/nyafaz-brand/productlist",
      permission: 19,
    },
    {
      key: "my-orders",
      icon: myorders,
      label: "My orders",
      to: "/nyafaz-brand/my-orders",
      userOnly: true,
    },
    {
      key: "all-users-info",
      icon: User,
      label: "My customers",
      to: "/nyafaz-brand/all-users-info",
      userOnly: true,
    },
    {
      key: "embed-script",
      icon: integicon,
      label: "Embed Script",
      to: "/nyafaz-brand/embed-script",
      userOnly: true,
      disabled: !embedEnabled,
    },
    {
      key: "whatsapp-template-manager",
      icon: <IoLogoWhatsapp aria-hidden />,
      label: "WhatsApp Setup",
      to: "/nyafaz-brand/whatsapp-template-manager",
      userOnly: true,
    },
    {
      key: "account-billing",
      icon: seticon,
      label: "Account & billing",
      to: "/nyafaz-brand/account-billing",
      userOnly: true,
      section: SIDEBAR_SECTIONS.ACCOUNT,
    },
    {
      key: "contact",
      icon: contacticon,
      label: "Contact us",
      to: "/nyafaz-brand/contact-us",
      userOnly: true,
    },
    {
      key: "permission",
      icon: permicon,
      label: "Permission",
      to: "/nyafaz-brand/organization-permission",
      adminOnly: true,
    },
    {
      key: "brand-users",
      icon: User,
      label: "Brand Users",
      to: "/nyafaz-brand/brand-users",
      adminOnly: true,
    },

    {
      key: "subscriptions",
      icon: subscription,
      label: "Subscriptions",
      to: "/nyafaz-brand/subscriptions",
      adminOnly: true,
    },
    {
      key: "payment-requests",
      icon: payment,
      label: "Payment Requests",
      to: "/nyafaz-brand/payment-requests",
      adminOnly: true,
    },
    {
      key: "invoices",
      icon: invoice,
      label: "Invoices",
      to: "/nyafaz-brand/invoices",
      adminOnly: true,
    },
    {
      key: "admin-contact-messages",
      icon: contacticon,
      label: "Contact Messages",
      to: "/nyafaz-brand/admin-contact-messages",
      adminOnly: true,
    },
    {
      key: "sales-team-create",
      icon: User,
      label: "Create Sales User",
      to: "/nyafaz-brand/sales-team/create",
      adminOnly: true,
      section: "Sales Team",
    },
    {
      key: "sales-team-users",
      icon: User,
      label: "Sales Users List",
      to: "/nyafaz-brand/sales-team/users",
      adminOnly: true,
      section: "Sales Team",
    },
    {
      key: "sales-team-shared-links",
      icon: payment,
      label: "Shared Links",
      to: "/nyafaz-brand/sales-team/shared-links",
      adminOnly: true,
      section: "Sales Team",
    },
    {
      key: "sales-team-request-users",
      icon: contacticon,
      label: "Request Users",
      to: "/nyafaz-brand/sales-team/request-users",
      adminOnly: true,
      section: "Sales Team",
    },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.disabled) return false;
    if (dashboardOnly) {
      return ["overview"].includes(item.key);
    }
    if (item.adminOnly && !permissionService.isPlatformAdmin()) return false;
    if (item.userOnly && !permissionService.showTenantUserMenus()) return false;

    if (!item.permission) return true;

    if (Array.isArray(item.permission)) {
      return permissionService.hasAny(item.permission);
    }

    return permissionService.has(item.permission);
  });

  return (
    <aside
      className={`fixed left-6 top-4 h-[calc(100vh-2rem)]
      flex flex-col items-center
      transition-all duration-500 ease-in-out
      sidebar-shell ${expanded ? "sidebar-shell--expanded" : "sidebar-shell--collapsed"}
      rounded-2xl
      z-50
      before:content-[''] before:absolute before:inset-0 before:rounded-2xl before:pointer-events-none before:border-[1px] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_28px),radial-gradient(circle_at_bottom_left,transparent_16px,black_28px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in]
    `}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="sidebar-icon-btn absolute -right-3 top-6
                  w-6 h-6 rounded-full 
                  flex items-center justify-center
                  transition shadow-[0_4px_12px_rgba(0,0,0,0.12)]"
      >
        {expanded ? <FiChevronLeft size={14} /> : <FiChevronRight size={14} />}
      </button>

      {/* LOGO */}
      <div className="flex flex-col items-center pt-4 select-none w-full">
        <img
          src={nyafaz}
          alt="Nyafaz Logo"
          className={`transition-all duration-300 object-contain
          ${expanded ? "w-24 h-auto" : "w-8 h-auto"}`}
        />
        <div className="sidebar-divider w-6 h-px mt-3"></div>
      </div>

      {/* SCROLL UP ARROW */}
      <div className="w-full flex justify-center pt-2">
        <button
          onClick={scrollUp}
          className="sidebar-text-faint transition-colors p-1"
          title="Scroll Up"
        >
          <FiChevronUp size={20} />
        </button>
      </div>

      {/* MENU */}
      <div 
        ref={menuRef}
        className="mt-2 flex-1 flex flex-col gap-3 w-full overflow-y-auto"
      >
        {filteredMenuItems.map((item, index) => {
          const previousSection = filteredMenuItems[index - 1]?.section;
          const showSectionLabel = expanded && item.section && item.section !== previousSection;

          return (
            <Fragment key={item.key}>
              {showSectionLabel ? (
                <div className="sidebar-text-muted px-5 pt-2 text-[11px] font-semibold uppercase tracking-[0.24em]">
                  {item.section}
                </div>
              ) : null}
              <div className="w-full flex justify-center">
                <NavLink
                  to={item.disabled ? "#" : item.to}
                  onClick={(e) => {
                    if (item.disabled) e.preventDefault();
                  }}
                  className={({ isActive }) =>
                    `sidebar-nav-link flex items-center duration-300 group
                    ${item.disabled ? "sidebar-nav-link--disabled opacity-45 cursor-not-allowed pointer-events-none" : ""}
                    ${isActive ? "sidebar-nav-link--active" : ""}
                    ${expanded
                      ? "w-[94%] ml-auto rounded-l-xl rounded-r-none px-4 py-2 justify-start mb-0.5"
                      : "w-10 h-10 rounded-xl justify-center"
                    }`
                  }
                  title={item.disabled ? "Temporarily disabled" : item.label}
                >
                  <div className={`flex items-center justify-center transition-all duration-300
                    ${expanded ? "w-7 h-7" : "w-8 h-8"}`}>
                    {item.IconComponent ? (
                      <item.IconComponent
                        className={`sidebar-menu-icon-img object-contain
                          ${expanded ? "w-5 h-5" : "w-6 h-6"}`}
                        strokeWidth={2.25}
                        aria-hidden="true"
                      />
                    ) : typeof item.icon === "string" ? (
                      <img
                        src={item.icon}
                        alt={item.label}
                        className={`sidebar-menu-icon-img object-contain
                          ${expanded ? "w-5 h-5" : "w-6 h-6"}`}
                      />
                    ) : (
                      <span className={`sidebar-menu-icon flex items-center justify-center
                        ${expanded ? "text-lg" : "text-xl"}`}>
                        {item.icon}
                      </span>
                    )}
                  </div>

                  {expanded && (
                    <span className="sidebar-text ml-4 text-sm font-medium whitespace-nowrap opacity-100 transition-opacity duration-300">
                      {item.label}
                    </span>
                  )}
                </NavLink>
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* SCROLL DOWN ARROW */}
      <div className="w-full flex justify-center pb-2">
        <button
          onClick={scrollDown}
          className="sidebar-text-faint transition-colors p-1"
          title="Scroll Down"
        >
          <FiChevronDown size={20} />
        </button>
      </div>

      <div className="w-full flex justify-center px-3 pb-2">
        <div
          className={`sidebar-text-muted text-[11px] font-medium tracking-wide transition-all ${expanded ? "w-[94%] ml-auto px-4 text-left" : "text-center"}`}
        >
          {expanded ? "Version 1.0.0" : "v1.0"}
        </div>
      </div>

      <div className="py-3 w-full flex justify-center">
        <button
          onClick={handleLogout}
          className={`sidebar-logout-btn sidebar-text flex items-center transition-all group
          ${expanded
              ? "w-[94%] ml-auto rounded-l-xl rounded-r-none px-4 py-2 justify-start"
              : "w-10 h-10 rounded-xl justify-center"}`}
        >
          <div className={`flex items-center justify-center transition-all
            ${expanded ? "w-7 h-7" : "w-8 h-8"}`}>
            <img
              src={logicon}
              alt="Logout"
              className={`sidebar-menu-icon-img object-contain
                ${expanded ? "w-4.5 h-4.5" : "w-5.5 h-5.5"}`}
            />
          </div>

          {expanded && (
            <span className="ml-4 text-sm font-medium opacity-100 whitespace-nowrap">Logout</span>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
